import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import usePrevious from 'shared/hooks/usePrevious';
import chatStore from 'shared/store/chatStore';
import './styles.css';

// Component imports
import { UserMenu } from 'shared/components/user/UserMenu';
import { MenuItemButton } from './MenuItemButton/MenuItemButton';

// Store imports
import useAppStatus from 'shared/store/appStatusStore';
import useThreadsStore from 'shared/store/threadsStore';
import activityPanelStore from 'shared/store/activityPanelStore';
import constraintsStore from 'shared/store/constraintsStore';
import colorsStore from 'shared/store/colorsStore';
import { useTabletAndBelowMediaQuery } from 'shared/store/responsiveStore';
import { hasAnyVisibleSectionAgentChange } from 'pages/assistant/components/activity-panel/panels/constraints/changeTracking';

// Icon imports
import { ThreadHistoryIcon } from 'shared/assets/svg/menu-icons/ThreadHistoryIcon';
import { PlusIcon } from 'shared/assets/svg/menu-icons/PlusIcon';
import { AttachmentsIcon } from 'shared/assets/svg/menu-icons/AttachmentsIcon';
import { ConstraintsIcon } from 'shared/assets/svg/menu-icons/ConstraintsIcon';
import { InstrumentListsIcon } from 'shared/assets/svg/menu-icons/InstrumentListsIcon';
import { GuidedPortfoliosIcon } from 'shared/assets/svg/menu-icons/GuidedPortfoliosIcon';
import guidedPortfoliosStore from 'shared/store/guidedPortfoliosStore';

// Utilities and configs
import { createNewChat } from 'utils/chatUtils';

// Styles
import styles from './MenuList.module.scss';

const BUBBLE_DURATION_MS = 4000;

interface MenuListProps {
  onClose?: () => void;
  isOpen?: boolean;
}

export const MenuList: React.FC<MenuListProps> = ({ onClose, isOpen = false }) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const isTabletOrBelow = useTabletAndBelowMediaQuery();
  const { t } = useTranslation();

  const setAppStatus = useAppStatus((state) => state.setAppStatus);
  const fromHistory = useThreadsStore((state) => state.fromHistory);
  const appStatus = useAppStatus((state) => state.appStatus);
  const guidedPortfoliosOpen = guidedPortfoliosStore((s) => s.open);

  const {
    attachmentsRedDot,
    setAttachmentsRedDot,
    userSentFirstMessage,
    newConstraintsAdded,
    setNewConstraintsAdded,
    constraintsPanelMode,
  } = activityPanelStore();

  // Vincoli menu-button red dot is a derived value: it stays on as long
  // as any section inside the panel still has an unacknowledged agent
  // change. Opening the panel no longer dismisses it — only acknowledging
  // (or clearing) every section does.
  const agentChangedPaths = constraintsStore((s) => s.agentChangedPaths);
  const acknowledgedSectionGroups = constraintsStore((s) => s.acknowledgedSectionGroups);
  const constraintsMainMenuRedDot = hasAnyVisibleSectionAgentChange(
    constraintsPanelMode,
    agentChangedPaths,
    acknowledgedSectionGroups,
  );

  const navigate = useNavigate();
  const [showConstraintsBubble, setShowConstraintsBubble] = useState(false);

  const { files } = chatStore();
  const prevFiles = usePrevious(files);

  // A new file only lights the «Allegati» red dot: a bubble beside the menu covered the first column of the
  // answer (the ISINs of the table the file was exported from).
  useEffect(() => {
    const fileListLengthChanged = prevFiles && files.length > prevFiles.length && appStatus !== 'attachments';
    if (!fromHistory && userSentFirstMessage && fileListLengthChanged) {
      setAttachmentsRedDot(true);
    }
  }, [files, fromHistory, userSentFirstMessage]);

  useEffect(() => {
    if (!fromHistory && userSentFirstMessage && newConstraintsAdded) {
      setShowConstraintsBubble(true);
    }
  }, [fromHistory, userSentFirstMessage, newConstraintsAdded]);

  // The hide timer is keyed on the bubble alone: tied to the trigger effect
  // above, any change of its deps (Nuova chat) cleared the timer and left the
  // bubble on screen for good.
  useEffect(() => {
    if (!showConstraintsBubble) return;
    const timeout = setTimeout(() => {
      setNewConstraintsAdded(false);
      setShowConstraintsBubble(false);
    }, BUBBLE_DURATION_MS);
    return () => clearTimeout(timeout);
  }, [showConstraintsBubble]);

  // A new chat (or another thread) resets the store: drop the previous chat's bubble at once.
  useEffect(() => {
    if (!userSentFirstMessage) {
      setShowConstraintsBubble(false);
    }
  }, [userSentFirstMessage]);

  return (
    <aside
      className={classNames(
        styles.appMenuContainer,
        'flex h-full w-24 flex-col justify-between transition-all duration-300 ease-in-out',
      )}
      style={{
        backgroundColor: colorsConfig.white,
        borderRight: `1px solid ${colorsConfig.grayLight}`,
      }}
    >
      <div className={styles.menuItemsContainer}>
        <MenuItemButton
          item={{
            icon: PlusIcon,
            name: 'newChat',
            activeIconColor: colorsConfig.finecoBluePrimary,
            inactiveIconColor: colorsConfig.finecoBlack,
            activeBackgroundColor: colorsConfig.iconActiveBackground,
            inactiveBackgroundColor: colorsConfig.secondBackground,
          }}
          active={false}
          onClick={() => {
            createNewChat(navigate);
            if (isTabletOrBelow && onClose) {
              onClose();
            }
          }}
        />
        <MenuItemButton
          item={{
            icon: ThreadHistoryIcon,
            name: 'threadHistory',
            activeIconColor: colorsConfig.finecoBluePrimary,
            inactiveIconColor: colorsConfig.finecoBlack,
            activeBackgroundColor: colorsConfig.iconActiveBackground,
            inactiveBackgroundColor: colorsConfig.secondBackground,
          }}
          active={appStatus === 'history'}
          onClick={() => {
            if (appStatus === 'history') {
              setAppStatus('idle');
            } else {
              setAppStatus('history');
            }
          }}
        />
        <div className="separator" style={{ backgroundColor: colorsConfig.GrayLight2 }} />
        <MenuItemButton
          id="attachments"
          item={{
            icon: AttachmentsIcon,
            name: 'attachments',
            activeIconColor: colorsConfig.finecoBluePrimary,
            inactiveIconColor: colorsConfig.finecoBlack,
            activeBackgroundColor: colorsConfig.iconActiveBackground,
            inactiveBackgroundColor: colorsConfig.secondBackground,
          }}
          active={appStatus === 'attachments'}
          onClick={() => {
            if (appStatus === 'attachments') {
              setAppStatus('idle');
            } else {
              setAttachmentsRedDot(false);
              setAppStatus('attachments');
            }
          }}
          showRedDot={attachmentsRedDot && files && files.length > 0}
        />

        <MenuItemButton
          id="constraints"
          item={{
            icon: ConstraintsIcon,
            name: 'constraints',
            activeIconColor: colorsConfig.finecoBluePrimary,
            inactiveIconColor: colorsConfig.finecoBlack,
            activeBackgroundColor: colorsConfig.iconActiveBackground,
            inactiveBackgroundColor: colorsConfig.secondBackground,
          }}
          active={appStatus === 'constraints'}
          onClick={() => {
            if (appStatus === 'constraints') {
              setAppStatus('idle');
            } else {
              setShowConstraintsBubble(false);
              // The menu-level red dot is derived from "any section dot
              // still visible" — we don't dismiss it on panel open. It
              // clears on its own when every section's per-group red dot
              // is acknowledged (via `acknowledgeSection`) or bulk-cleared
              // (Reset / applyProfile / applyPredefiniti / Invia).
              setAppStatus('constraints');
            }
          }}
          isDisabled={constraintsPanelMode === 'diagnosis'}
          showRedDot={constraintsMainMenuRedDot}
          bubbleText={t('menu.newConstraintsAdded')}
          showBubble={showConstraintsBubble}
          handleHideBubble={() => setShowConstraintsBubble(false)}
        />

        {constraintsPanelMode !== 'diagnosis' && (
          <>
            <div className="separator" style={{ backgroundColor: colorsConfig.GrayLight2 }} />
            <MenuItemButton
              id="instrument-lists"
              item={{
                icon: InstrumentListsIcon,
                name: 'instrumentLists',
                activeIconColor: colorsConfig.finecoBluePrimary,
                inactiveIconColor: colorsConfig.finecoBlack,
                activeBackgroundColor: colorsConfig.iconActiveBackground,
                inactiveBackgroundColor: colorsConfig.secondBackground,
              }}
              active={appStatus === 'instrument-lists'}
              onClick={() => {
                if (appStatus === 'instrument-lists') {
                  setAppStatus('idle');
                } else {
                  setAppStatus('instrument-lists');
                }
              }}
              isDisabled={false}
            />
            <MenuItemButton
              id="guided-portfolios"
              item={{
                icon: GuidedPortfoliosIcon,
                name: 'guidedPortfolios',
                activeIconColor: colorsConfig.finecoBluePrimary,
                inactiveIconColor: colorsConfig.finecoBlack,
                activeBackgroundColor: colorsConfig.iconActiveBackground,
                inactiveBackgroundColor: colorsConfig.secondBackground,
              }}
              active={guidedPortfoliosOpen}
              onClick={() => {
                guidedPortfoliosStore.getState().setOpen(true);
                if (isTabletOrBelow && onClose) {
                  onClose();
                }
              }}
              // fineco_analyze_model_portfolio only works in builder mode
              isDisabled={constraintsPanelMode !== 'builder'}
            />
          </>
        )}
      </div>

      <div className={styles.menuUserItemContainer}>
        <UserMenu />
      </div>
    </aside>
  );
};
